/**
 * Backend ``detail.error_kind`` → operator-visible toast copy, for the
 * MatchGraph manual line-link drag handler (#1087).
 *
 * The backend endpoint at
 * ``backend/src/api/v1/endpoints/twin/match_groups.py`` raises
 * ``HTTPException(detail={"error_kind": "…"})`` for every expected
 * user-facing failure on POST ``/v1/twin/match-groups/line-link``.
 * The ``error_kind`` strings live in
 * ``backend/src/services/match_group_service.py`` (link_conflict /
 * unlink_conflict) plus the endpoint wrapper (concurrent_line_link,
 * match_group_not_found, match_state_read_failed, line_not_found).
 * Whenever a new ``error_kind`` is added there, mirror it here AND
 * extend ``lineLinkErrorToast.test.ts`` with the new mapping so a
 * missed translation falls back to the generic copy instead of leaking
 * a code to the operator.
 *
 * Strings are in German because the gutschrift-check surface is
 * hardcoded-German end-to-end (see neighbouring inline strings in
 * GutschriftCheckPage.tsx — there is no i18n key for this page).
 */
import { AxiosError } from 'axios';

const GENERIC_CLIENT_ERROR =
  'Verknüpfung fehlgeschlagen – bitte erneut versuchen.';
const GENERIC_SERVER_ERROR = 'Serverfehler – bitte erneut versuchen.';

const ERROR_KIND_MESSAGES: Record<string, string> = {
  concurrent_line_link:
    'Eine andere Änderung läuft gerade – bitte gleich erneut versuchen.',
  // #1391 — the ``third_party_match`` error_kind was removed: both
  // sides now fan out (one Gutschrift line may aggregate several
  // Lieferschein lines and vice versa), so a link never conflicts.
  // The mapping is gone; an unexpected ``third_party_match`` from an
  // old backend falls through to the generic 4xx copy rather than
  // leaking a message that no longer reflects the model.
  stale_unlink:
    'Ansicht ist veraltet – bitte aktualisieren und erneut versuchen.',
  match_group_not_found: 'Match-Gruppe wurde verschoben oder gelöscht.',
  // #1086 (v22.3.0) added a pre-lock existence check on each request's
  // ``delivery_line_id`` / ``settlement_line_id``. If either points at
  // an ``event_line`` entity that's been re-materialised under a
  // different ``event_id:idx`` shape (e.g. after a parallel worker
  // pass), the endpoint 404s here with a ``missing: [...]`` payload.
  // Before #1120 the FE fell through to the generic 4xx copy and the
  // operator saw "Verknüpfung fehlgeschlagen" — indistinguishable from
  // a network blip, which read to operators as a silent drop.
  line_not_found:
    'Verknüpfung nicht möglich: Eine der Warenzeilen ist nicht mehr verfügbar – bitte Seite aktualisieren.',
  // HOF-219 — the document-level route's own kinds. Same module because the
  // two endpoints share every other failure kind above and an operator cannot
  // tell which layer refused them; a second mapper would be a second place to
  // forget a translation.
  concurrent_document_link:
    'Eine andere Änderung an dieser Gruppe läuft gerade – bitte gleich erneut versuchen.',
  event_not_found:
    'Trennen nicht möglich: Einer der beiden Belege ist nicht mehr verfügbar – bitte Seite aktualisieren.',
  // The exclusion is the durable half of the sever. If it did not land, nothing
  // was severed — saying "try again" is the honest instruction, because the
  // fact was deliberately left untouched.
  unlinked_delivery_write_failed:
    'Trennung konnte nicht gespeichert werden – bitte erneut versuchen.',
};

/**
 * Reads ``detail.error_kind`` off the rejected promise's AxiosError
 * and returns the operator-visible string. Defensive against every
 * non-axios shape (plain Error, thrown string, undefined) — pathologic
 * but possible, and the handler must never throw from inside its own
 * ``.catch``.
 */
export function mapLineLinkErrorToToastMessage(err: unknown): string {
  if (!(err instanceof AxiosError)) {
    return GENERIC_SERVER_ERROR;
  }

  const status = err.response?.status;
  if (status === undefined) {
    // Network error, timeout, CORS — no HTTP response to read.
    return GENERIC_SERVER_ERROR;
  }

  // HOF-219 — a MAPPED ``error_kind`` now wins over the status band, where it
  // used to be read only after the ``>= 500`` branch had already returned.
  // ``unlinked_delivery_write_failed`` is a 502 that carries a specific, useful
  // instruction ("nothing was severed, try again"), and under the old order it
  // could only ever surface as "Serverfehler".
  //
  // A strict EXTENSION, not a reordering: every kind in the table is a 4xx the
  // old order already reached, and an UNMAPPED kind still falls through to the
  // same two generic branches below, in the same order. The only reachable
  // difference is a mapped kind arriving on a 5xx — which is precisely the case
  // this change exists to serve.
  const detail = err.response?.data?.detail;
  if (detail && typeof detail === 'object' && 'error_kind' in detail) {
    const kind = (detail as { error_kind?: unknown }).error_kind;
    if (typeof kind === 'string' && kind in ERROR_KIND_MESSAGES) {
      return ERROR_KIND_MESSAGES[kind];
    }
  }

  if (status >= 500) {
    return GENERIC_SERVER_ERROR;
  }

  return GENERIC_CLIENT_ERROR;
}

/**
 * Wraps a manual-link ``mutateAsync`` call so:
 *
 * 1. Backend rejections surface as a toast (operator-readable copy
 *    via ``mapLineLinkErrorToToastMessage``) instead of leaking as
 *    unhandled promise rejections in the browser console.
 * 2. The optimistic edge is cleared on every outcome (success,
 *    expected rejection, network error) — preserves the original
 *    ``.finally`` semantics.
 *
 * Extracted from ``MatchGraph.handleConnect`` so the promise chain
 * can be pinned in a Vitest unit test without mounting ReactFlow.
 * Returns ``Promise<void>`` (always resolves) so callers using
 * ``void`` don't need their own ``.catch`` — the toast IS the user
 * feedback.
 */
export async function runManualLinkWithToast(args: {
  call: () => Promise<unknown>;
  showToast: (message: string, tone: 'error') => void;
  clearOptimisticEdge: () => void;
}): Promise<void> {
  try {
    // ``await`` instead of ``return`` so a synchronous throw from
    // ``args.call()`` (defensive: a mutationFn that errors before
    // returning a promise) routes through the ``catch`` + ``finally``
    // arms below, not past them.
    await args.call();
  } catch (err) {
    args.showToast(mapLineLinkErrorToToastMessage(err), 'error');
  } finally {
    args.clearOptimisticEdge();
  }
}
